import { z } from 'zod';
import { SchedulePresetSchema } from '@wit-support/pipeline';
import { ReplyTemplateId } from './templates.js';
import { ConversationId } from './conversations.js';
import { PipelineId } from './pipelines.js';
/**
 * SINGLE SOURCE for the schedule-preset enum: re-exported from
 * `@wit-support/pipeline` (`SchedulePresetSchema`). admin-contracts already
 * depends on `@wit-support/pipeline` (see `pipelines.ts`) and pipeline does NOT
 * depend on admin-contracts, so there is no dependency cycle. The duplicate
 * enum that used to live here was deleted to kill the silent-drift hazard at the
 * source. The ms mapping (`PRESET_MS`) is admin-contracts' concern; a parity
 * test (`scheduled-email.test.ts`) asserts its keys exactly equal the pipeline
 * enum options, and `resolvePresetMs` throws on an unknown key as a second guard.
 */
export const SchedulePreset = SchedulePresetSchema;
export const PRESET_MS = {
    '+30m': 30 * 60_000,
    '+1d': 86_400_000,
    '+2d': 2 * 86_400_000,
    '+3d': 3 * 86_400_000,
    '+4d': 4 * 86_400_000,
};
/**
 * Map a preset to its millisecond delay. Throws on an unknown key rather than
 * returning `undefined` — an `undefined` would silently flow into
 * `new Date(Date.now() + undefined)` = Invalid Date and be written as
 * `scheduled_for`. The throw makes any drift loud at the call site.
 */
export const resolvePresetMs = (p) => {
    const ms = PRESET_MS[p];
    if (ms == null)
        throw new Error(`unknown schedule preset: ${p}`);
    return ms;
};
/**
 * Exactly one of `{ preset }` or `{ at }`. Same two strict branches as before —
 * but a `.superRefine` over `z.unknown()` so a malformed body returns ONE clear
 * top-level message ('scheduleFor must be exactly one of { preset } or { at }')
 * instead of Zod's noisy flattened both-branches union error (which lists every
 * failed key of every branch). Validation behavior is IDENTICAL: a valid
 * `{ preset }` or `{ at:<rfc3339> }` passes (and is returned strongly typed via
 * the transform); anything else — both keys, neither key, extra keys, bad
 * datetime — is rejected with the single message above.
 */
export const SchedulePresetBranch = z.object({ preset: SchedulePreset }).strict();
export const ScheduleAtBranch = z
    .object({
    at: z.string().datetime({ offset: true }),
})
    .strict();
export const ScheduleFor = z
    .unknown()
    .superRefine((v, ctx) => {
    if (SchedulePresetBranch.safeParse(v).success || ScheduleAtBranch.safeParse(v).success)
        return;
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'scheduleFor must be exactly one of { preset } or { at }',
    });
})
    // After the refine guarantees exactly one valid branch, narrow `unknown` to the
    // discriminated output type so downstream consumers keep the precise type.
    .transform((v) => v);
/**
 * Schedule-an-email request schema. NOTE for API-client authors hand-building a
 * request BODY: type your body with {@link CreateScheduledEmailInput}
 * (`z.input<...>`), NOT the default `z.infer` export below. The default
 * `CreateScheduledEmailRequest` type is the POST-TRANSFORM *output* shape (both
 * `subject` and `bodyHtml` present, blank-normalized to `undefined`), which does
 * not match the request body a client constructs (where `subject` may be `null`
 * and the template branch omits `bodyHtml`). Note: only `subject` is nullable
 * (`.nullable().optional()`); `bodyHtml` is `string | undefined` — never null.
 * Parse INPUT with this schema on the server; type OUTGOING bodies with
 * `CreateScheduledEmailInput`.
 */
export const CreateScheduledEmailRequest = z
    .object({
    templateId: ReplyTemplateId.optional(),
    subject: z.string().max(500).nullable().optional(),
    bodyHtml: z.string().max(200_000).optional(),
    scheduleFor: ScheduleFor,
})
    .strict()
    // Normalize blank/whitespace-only subject + bodyHtml to `undefined` BEFORE the
    // refine + before the DB write. Without this a whitespace-only subject ('  ')
    // alongside a templateId slips past the refine and then violates the DB
    // `scheduled_email_content_ck` (template branch requires subject NULL) → 500.
    .transform((v) => ({
    ...v,
    subject: v.subject != null && v.subject.trim().length > 0 ? v.subject : undefined,
    bodyHtml: v.bodyHtml != null && v.bodyHtml.trim().length > 0 ? v.bodyHtml : undefined,
}))
    .refine((v) => {
    // Runs POST-transform: the preceding `.transform` already normalized
    // whitespace-only subject/bodyHtml to `undefined`, so a plain
    // `!= null` check is sufficient (no re-trim needed).
    // Mirror DB `scheduled_email_content_ck`:
    //   template branch: template_id set, subject NULL, body_html NULL
    //   custom branch:   subject AND body_html both set
    const hasBody = v.bodyHtml != null;
    if (v.templateId != null) {
        // Template branch — forbid any subject or body alongside it.
        if (hasBody)
            return false;
        if (v.subject != null)
            return false;
        return true;
    }
    // Custom branch — BOTH subject and body required (mirrors the tightened
    // DB CHECK that requires a non-null subject on a custom scheduled email).
    return hasBody && v.subject != null;
}, {
    message: 'Provide exactly one of templateId or { subject + bodyHtml }; templateId must not carry subject or bodyHtml',
});
export const ScheduledEmailStatus = z.enum(['pending', 'enqueued', 'sent', 'cancelled', 'failed']);
export const ScheduledEmailSource = z.enum(['admin', 'telegram', 'pipeline']);
// Mirrors the DB CHECK `scheduled_email_cancel_reason_check` (migration
// 20260917120100). `merge_duplicate` is written by the merge engine
// (`ConversationMergePg.mergeMany`) when a source's pending email duplicates
// the target's template — the DTO must accept it or the queue panel drops the row.
export const ScheduledEmailCancelReason = z.enum([
    'manual',
    'blacklist',
    'duplicate_template',
    'merge_duplicate',
]);
/**
 * Response DTO schema — a TYPE SOURCE only. The server shapes responses via `as`
 * casts (mapping DB rows) and never `.parse()`s these schemas at the route
 * boundary, matching the repo-wide read-path pattern (the underlying columns are
 * DB-CHECK-constrained, so the shape is enforced at the DB layer). Do NOT add a
 * per-route `.parse()` here — that would be inconsistent with every other read
 * route. This schema exists for `z.infer` (and ad-hoc client-side validation).
 */
export const ScheduledEmailDTO = z.object({
    id: z.guid(),
    // Branded ids so the same nominal types flow through read + write paths
    // (the request side already uses ReplyTemplateId/ConversationId/PipelineId).
    conversationId: ConversationId,
    status: ScheduledEmailStatus,
    // `{ offset: true }` for uniformity with request-side ScheduleAtBranch. The
    // server always emits Date.toISOString() = 'Z' (UTC), so responses are always
    // valid under both `offset:true` and the stricter default. Keeping the posture
    // uniform avoids silent contract drift if a future client passes an offset form.
    scheduledFor: z.string().datetime({ offset: true }),
    templateId: ReplyTemplateId.nullable(),
    templateName: z.string().nullable(),
    subject: z.string().nullable(),
    source: ScheduledEmailSource,
    pipelineId: PipelineId.nullable(),
    pipelineName: z.string().nullable(),
    cancelReason: ScheduledEmailCancelReason.nullable(),
    cancelledAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    sentAt: z.string().datetime({ offset: true }).nullable(),
});
/**
 * Global-list response DTO — same type-only contract as {@link ScheduledEmailDTO}:
 * a type source, NOT `.parse()`'d at the route boundary (DB-CHECK-constrained
 * columns; cast-shaped responses per the repo-wide read-path pattern).
 */
export const GlobalScheduledEmailDTO = ScheduledEmailDTO.extend({
    conversationSubject: z.string().nullable(),
    contactEmail: z.string().nullable(),
});
//# sourceMappingURL=scheduled-email.js.map